import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  amendmentService,
  type AmendmentDraftInput,
} from "../services/amendment.service";
import type { ReportPreviewEvidence } from "../services/signOff.service";

export const amendmentDraftQueryKey = (caseId: string) => [
  "case",
  caseId,
  "amendment-draft",
];

export function useAmendmentDraft(caseId: string, enabled = true) {
  return useQuery({
    queryKey: amendmentDraftQueryKey(caseId),
    queryFn: () => amendmentService.getDraft(caseId),
    enabled: enabled && Boolean(caseId),
  });
}

export function useAmendmentOptions(caseId: string, enabled = true) {
  return useQuery({
    queryKey: ["case", caseId, "amendment-options"],
    queryFn: () => amendmentService.getOptions(caseId),
    enabled: enabled && Boolean(caseId),
  });
}

export function useSaveAmendmentDraft(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      draftId,
      input,
    }: {
      draftId?: string;
      input: AmendmentDraftInput;
    }) =>
      draftId
        ? amendmentService.updateDraft(caseId, draftId, input)
        : amendmentService.createDraft(caseId, input),
    onSuccess: (draft, request) => {
      const current = queryClient.getQueryData<{ id: string } | null>(
        amendmentDraftQueryKey(caseId)
      );
      if (!request.draftId || current?.id === draft.id)
        queryClient.setQueryData(amendmentDraftQueryKey(caseId), draft);
    },
  });
}

export function useDeleteAmendmentDraft(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      amendmentService.deleteDraft(caseId, draftId),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: amendmentDraftQueryKey(caseId),
      });
      const draft = queryClient.getQueryData(amendmentDraftQueryKey(caseId));
      queryClient.setQueryData(amendmentDraftQueryKey(caseId), null);
      return { draft };
    },
    onError: (_error, _id, context) => {
      queryClient.setQueryData(amendmentDraftQueryKey(caseId), context?.draft);
    },
    onSuccess: () => {
      queryClient.setQueryData(amendmentDraftQueryKey(caseId), null);
    },
  });
}

export function useSignOutAmendment(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      draftId,
      revision,
      sessionId,
      password,
      previewEvidence,
    }: {
      draftId: string;
      revision: number;
      sessionId?: string | null;
      password?: string;
      previewEvidence?: ReportPreviewEvidence;
    }) =>
      amendmentService.signOutDraft(caseId, draftId, {
        revision,
        sessionId,
        password,
        previewEvidence,
      }),
    onSuccess: () => {
      queryClient.setQueryData(amendmentDraftQueryKey(caseId), null);
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({
        queryKey: ["document-view", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["sign-off-history", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["report-delivery-recall", caseId],
      });
    },
  });
}
